/**
 * features/reports/LiveReportPreview.tsx
 * ------------------------------------------------------------------
 * §3 — a live, in-app preview of a single student's report booklet.
 * Rebuilds the actual jsPDF (buildSingleReportPdf — the same builder
 * used for the stored file) whenever the editable content changes and
 * shows it in an iframe, so an editor sees the printed page update as
 * they type. Debounced so keystrokes don't rebuild on every character.
 * Gated by `canPreviewReports` at the call site.
 * ------------------------------------------------------------------
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  buildSingleReportPdf,
  buildAcademicPreviewPdf,
  type ReportStudent,
  type RenderOptions,
} from '@/lib/reportPdf';

export default function LiveReportPreview({
  student,
  options,
  className = '',
  academicOnly = false,
}: {
  student: ReportStudent;
  options: RenderOptions;
  className?: string;
  /** Render only the inner academic pages (marks/grades/remarks) — used by the
   *  all-teacher live View Reports view, which never shows the cover/back. */
  academicOnly?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [building, setBuilding] = useState(true);
  const urlRef = useRef<string | null>(null);

  // Rebuild when the edited content changes. The editor edits the two
  // remark comments, so those strings (plus the student identity) are the
  // rebuild triggers; `options` is read fresh from the closure at build time.
  useEffect(() => {
    setBuilding(true);
    const t = setTimeout(() => {
      try {
        const pdf = academicOnly
          ? buildAcademicPreviewPdf(student, options)
          : buildSingleReportPdf(student, options);
        const blob = pdf.output('blob');
        const u = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = u;
        setUrl(u);
      } catch {
        /* transient build error while typing — keep the last good preview */
      } finally {
        setBuilding(false);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    student.name,
    student.classTeacherComment,
    student.hodComment,
    student.subjects.length,
    academicOnly,
  ]);

  // Revoke the last object URL on unmount.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  return (
    <div className={`relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900 ${className}`}>
      {url ? (
        <iframe title="Report preview" src={url} className="w-full h-full border-0" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-sm text-gray-400">
          <Loader2 size={16} className="animate-spin mr-2" /> Building preview…
        </div>
      )}
      {building && url && (
        <div className="absolute top-2 right-2 flex items-center gap-1 text-[11px] text-gray-500 bg-white/80 dark:bg-gray-800/80 px-2 py-0.5 rounded-full">
          <Loader2 size={11} className="animate-spin" /> updating
        </div>
      )}
    </div>
  );
}
